import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Modal } from '../ui/Modal';
import { InputWithClear } from '../ui/InputWithClear';
import { AccountTypeahead, clientTypeaheadLabel } from '../accounts/AccountTypeahead';
import { DemTypeahead } from '../dem/DemTypeahead';
import { useCrmData } from '../../contexts/CrmDataContext';
import { usePortal } from '../../contexts/PortalContext';
import { useModalFormSession } from '../../hooks/useModalFormSession';
import { canAssignProjectTaskOwner, canSelectContactsForLinkedAccount, canViewAllAccounts } from '../../lib/accountVisibility';
import { resolveAccountDisplayAddress } from '../../lib/acraRegistry';
import { TEMP_ACCOUNT_CODE_PREFIX, isTempAccountCode } from '../../lib/accountCode';
import { demMainCustomerCode } from '../../lib/demCustomers';
import { ensureProspectAccount } from '../../lib/createProspectAccount';
import {
  formatOutletChoiceLabel,
} from '../../lib/accountPurchaseHistory';
import { formatSupabaseError } from '../../lib/formatSupabaseError';
import {
  activityChangeLine,
  buildActivityChangeDetail,
  logPortalActivityQuiet,
} from '../../lib/portalActivityLog';
import { getDbSupabase } from '../../lib/supabase';
import type { Contact, Task } from '../../types/crm';
import { TASK_STATUSES } from '../../types/crm';
import {
  clientById,
  clientsForTaskProjectLinking,
  idsMatch,
  ownerOptionMatches,
} from '../../utils/crmHelpers';
import {
  clientMatchesSalespersonCodes,
  formatSalespersonCode,
  formatSalespersonCodes,
  normalizeSalespersonCode,
  normalizeSalespersonCodes,
} from '../../utils/salespersonScope';
import { ownerIdForUser, ownerLabelForRef } from '../../utils/ownerId';
import { singaporeTaskLocationOptions } from '../../utils/singaporeTaskLocations';
import { DueDateTimeField } from './DueDateTimeField';
import { TaskAttachmentsField } from './TaskAttachmentsField';
import { IntlPhoneField } from '../forms/IntlPhoneField';
import { formatPhoneDisplay, normalizeStoredPhone, phoneInputValue } from '../../lib/intlPhone';
import { uploadPendingTaskAttachments } from '../../lib/taskAttachments';
import {
  datetimeLocalSingaporeToIso,
  defaultDatetimeLocalSingapore,
  parseDueDateInput,
  TASK_TITLE_OPTIONS,
  toDatetimeLocalSingapore,
} from '../../utils/taskHelpers';

interface TaskFormModalProps {
  open: boolean;
  onClose: () => void;
  task?: Task | null;
  defaultClientId?: string;
  lockClient?: boolean;
  /** Visitation prospect — show as TEMP account, not searchable in CRM. */
  defaultProspectName?: string;
  lockProspect?: boolean;
  defaultTitle?: string;
  defaultDescription?: string;
  defaultAddress?: string;
  defaultDueDate?: string;
  defaultMeetingMinuteId?: string;
  onSaved?: (task: Task) => void;
}

function contactDisplayName(contact: Contact): string {
  return `${contact.first_name} ${contact.last_name}`.trim();
}

export function TaskFormModal({
  open,
  onClose,
  task,
  defaultClientId,
  lockClient = false,
  defaultProspectName,
  lockProspect = false,
  defaultTitle,
  defaultDescription,
  defaultAddress,
  defaultDueDate,
  defaultMeetingMinuteId,
  onSaved,
}: TaskFormModalProps) {
  const { kind: portal } = usePortal();
  const {
    data,
    patchTask,
    refresh,
    username,
    ownerIdKind,
    ownerOptions,
    effectiveRole,
    departmentSlug,
    ownSalespersonCodes,
    actorSalespersonCodes,
    masterSalespersonCodes,
  } = useCrmData();

  const elevatedAccountCreate = canViewAllAccounts(effectiveRole, departmentSlug);
  const canAssignOwner = canAssignProjectTaskOwner(effectiveRole, departmentSlug);

  /** Elevated: User Management master list. Others: only their assigned codes. */
  const salespersonChoices = useMemo(() => {
    if (elevatedAccountCreate && masterSalespersonCodes.length > 0) {
      return masterSalespersonCodes;
    }
    return formatSalespersonCodes(ownSalespersonCodes);
  }, [elevatedAccountCreate, masterSalespersonCodes, ownSalespersonCodes]);

  const linkableClients = useMemo(
    () => clientsForTaskProjectLinking(data.clients),
    [data.clients],
  );

  const assignableOwners = useMemo(
    () => ownerOptions.filter((o) => o.username.toLowerCase() !== 'system'),
    [ownerOptions],
  );

  const titleOptions = useMemo(
    () => TASK_TITLE_OPTIONS.map((title) => ({ value: title, label: title })),
    [],
  );

  const isEdit = Boolean(task?.id);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [title, setTitle] = useState('');
  const [dueLocal, setDueLocal] = useState('');
  const [status, setStatus] = useState<string>(TASK_STATUSES[0]);
  const [clientId, setClientId] = useState('');
  const [prospectName, setProspectName] = useState('');
  const [description, setDescription] = useState('');
  const [ownerId, setOwnerId] = useState('');
  const [meetingMinuteId, setMeetingMinuteId] = useState('');
  const [contactPerson, setContactPerson] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [address, setAddress] = useState('');
  const [outletName, setOutletName] = useState('');
  const [outletInput, setOutletInput] = useState('');
  const [salespersonCode, setSalespersonCode] = useState('');
  const [accountPickerOpen, setAccountPickerOpen] = useState(false);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  /** Track whether address was manually edited so account changes don't overwrite. */
  const addressTouchedRef = useRef(false);
  /** User cleared / is creating a new account — don't restore task.client_id. */
  const accountClearedByUserRef = useRef(false);

  const linkedAccount = useMemo(
    () => (clientId ? clientById(linkableClients, clientId) : undefined),
    [clientId, linkableClients],
  );
  const hasLinkedAccount = Boolean(clientId);
  const hasProspectDraft = Boolean(prospectName.trim()) && !clientId;

  const showNewCustomerFields = Boolean(prospectName.trim() && !clientId) || lockProspect;
  const showSalespersonPicker = showNewCustomerFields;

  const accountIsOwnSalesCode = useMemo(
    () => clientMatchesSalespersonCodes(linkedAccount?.owner_id, actorSalespersonCodes),
    [linkedAccount?.owner_id, actorSalespersonCodes],
  );

  const canPickCrmContacts = canSelectContactsForLinkedAccount(
    effectiveRole,
    departmentSlug,
    !clientId || accountIsOwnSalesCode,
  );

  const accountContacts = useMemo(() => {
    if (!clientId || !canPickCrmContacts) return [];
    return data.contacts.filter((c) => idsMatch(c.client_id, clientId));
  }, [data.contacts, clientId, canPickCrmContacts]);

  const contactOptions = useMemo(
    () =>
      accountContacts.map((c) => {
        const name = contactDisplayName(c);
        return {
          value: name,
          label: c.phone ? `${name} · ${formatPhoneDisplay(c.phone)}` : name,
        };
      }),
    [accountContacts],
  );

  const matchedContact = useMemo(() => {
    const q = contactPerson.trim().toLowerCase();
    if (!q) return null;
    return accountContacts.find((c) => contactDisplayName(c).toLowerCase() === q) ?? null;
  }, [contactPerson, accountContacts]);

  const isNewContact = Boolean(contactPerson.trim()) && !matchedContact;
  const showContactPhone =
    Boolean(contactPerson.trim()) && (isNewContact || !matchedContact?.phone?.trim());

  const sessionKey = `${task?.id ?? 'new'}:${defaultClientId ?? ''}:${defaultProspectName ?? ''}:${defaultTitle ?? ''}:${defaultDescription ?? ''}:${defaultAddress ?? ''}:${defaultDueDate ?? ''}:${defaultMeetingMinuteId ?? ''}`;

  const defaultOwnerValue = useCallback(() => {
    const me = data.users.find((u) => u.username === username);
    if (isEdit && task?.owner_id) {
      const match = assignableOwners.find((o) => ownerOptionMatches(o, task.owner_id!));
      return match?.value ?? task.owner_id;
    }
    if (me) return ownerIdForUser(me, ownerIdKind);
    return username;
  }, [data.users, username, isEdit, task?.owner_id, assignableOwners, ownerIdKind]);

  const initForm = useCallback(() => {
    setTitle(task?.title ?? defaultTitle ?? '');
    setDueLocal(
      task?.due_date
        ? toDatetimeLocalSingapore(task.due_date)
        : defaultDueDate
          ? toDatetimeLocalSingapore(defaultDueDate)
          : defaultDatetimeLocalSingapore(),
    );
    setStatus(task?.status ?? TASK_STATUSES[0]);
    const preferred = task?.client_id ?? defaultClientId ?? '';
    const valid =
      preferred && linkableClients.some((c) => idsMatch(c.id, preferred)) ? String(preferred) : '';
    setClientId(valid);

    const prospect =
      task?.prospect_company_name?.trim() ||
      (lockProspect ? defaultProspectName?.trim() : '') ||
      '';
    setProspectName(prospect);

    setDescription(task?.description ?? defaultDescription ?? '');
    setOwnerId(defaultOwnerValue());
    setMeetingMinuteId(task?.meeting_minute_id ?? defaultMeetingMinuteId ?? '');
    setContactPerson(task?.contact_person ?? '');
    setContactPhone(phoneInputValue(task?.contact_phone));
    const accountForAddress = valid ? clientById(linkableClients, valid) : undefined;
    setAddress(
      task?.address?.trim() ||
        defaultAddress?.trim() ||
        resolveAccountDisplayAddress(accountForAddress) ||
        '',
    );
    addressTouchedRef.current = Boolean(task?.address?.trim() || defaultAddress?.trim());
    setOutletName(task?.outlet_name?.trim() || '');
    setOutletInput(
      task?.outlet_name?.trim() ||
        (task?.outlet_code
          ? formatOutletChoiceLabel(
              {
                code: task.outlet_code.trim(),
                name: task.outlet_name?.trim() || task.outlet_code.trim(),
                postal: task.outlet_postal?.trim() || '',
              },
              task?.client_id
                ? clientById(linkableClients, task.client_id)?.code || ''
                : '',
            ) || task.outlet_code.trim()
          : ''),
    );
    const preferredSp = ownSalespersonCodes[0] ?? salespersonChoices[0] ?? '';
    setSalespersonCode(preferredSp);
    setAccountPickerOpen(false);
    setPendingFiles([]);
    accountClearedByUserRef.current = false;
    setError(null);
  }, [
    task,
    defaultClientId,
    defaultProspectName,
    lockProspect,
    defaultTitle,
    defaultDescription,
    defaultAddress,
    defaultDueDate,
    defaultMeetingMinuteId,
    linkableClients,
    defaultOwnerValue,
    ownSalespersonCodes,
    salespersonChoices,
  ]);

  useModalFormSession(open, sessionKey, initForm);

  const motherNavCode = useMemo(() => {
    const code = linkedAccount?.code?.trim() || '';
    if (!code || isTempAccountCode(code)) return '';
    // CRM may store K0016-1 while NAV mother codes are K0016.
    return demMainCustomerCode(code) || code;
  }, [linkedAccount?.code]);

  const outletTypeaheadOptions = useMemo(() => singaporeTaskLocationOptions(), []);

  // Linked NAV account → always show searchable SG area / mall / MRT list (no outlet fetch).
  const showOutletField = Boolean(motherNavCode);

  function applyOutletInput(raw: string) {
    const trimmed = raw.trim();
    setOutletInput(raw);
    if (!trimmed) {
      setOutletName('');
      return;
    }

    const match = outletTypeaheadOptions.find(
      (o) => o.value.toLowerCase() === trimmed.toLowerCase(),
    );
    // Location catalog only — store as free-text name (no NAV outlet code).
    setOutletName(match?.value ?? trimmed);
    if (match) setOutletInput(match.value);
  }

  useEffect(() => {
    if (!open || clientId || accountClearedByUserRef.current) return;
    const preferred = task?.client_id ?? defaultClientId ?? '';
    const valid =
      preferred && linkableClients.some((c) => idsMatch(c.id, preferred))
        ? String(preferred)
        : '';
    if (valid) setClientId(valid);
  }, [open, clientId, linkableClients, task?.client_id, defaultClientId]);

  function applyClientId(nextId: string) {
    if (nextId) accountClearedByUserRef.current = false;
    setClientId(nextId);
    setOutletName('');
    setOutletInput('');
    if (nextId) {
      setProspectName('');
      setContactPerson('');
      setContactPhone('');
      setAccountPickerOpen(false);
    }
    if (!addressTouchedRef.current || !address.trim()) {
      const account = nextId ? clientById(linkableClients, nextId) : undefined;
      setAddress(resolveAccountDisplayAddress(account));
      addressTouchedRef.current = false;
    }
  }

  function applyNewAccountName(name: string) {
    const trimmed = name.trim();
    setProspectName(trimmed);
    if (trimmed) {
      accountClearedByUserRef.current = true;
      setClientId('');
      setOutletName('');
      setOutletInput('');
      setContactPerson('');
      setContactPhone('');
      setAccountPickerOpen(false);
    }
  }

  function clearRelatedAccount() {
    accountClearedByUserRef.current = true;
    applyClientId('');
    applyNewAccountName('');
    setAccountPickerOpen(true);
  }

  function startAccountChange() {
    accountClearedByUserRef.current = true;
    applyClientId('');
    applyNewAccountName('');
    setAccountPickerOpen(true);
  }

  function handleContactCommit(name: string) {
    const trimmed = name.trim();
    setContactPerson(trimmed);
    const match = accountContacts.find(
      (c) => contactDisplayName(c).toLowerCase() === trimmed.toLowerCase(),
    );
    setContactPhone(phoneInputValue(match?.phone));
  }

  function resolveOwnerId(): string {
    if (canAssignOwner && ownerId.trim()) return ownerId.trim();
    return defaultOwnerValue();
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    const trimmedTitle = title.trim();
    if (!trimmedTitle) {
      setError('Task title is required.');
      return;
    }

    const trimmedDescription = description.trim();
    if (!trimmedDescription) {
      setError('Description / notes is required.');
      return;
    }

    const trimmedProspect = prospectName.trim();
    let resolvedClientId = clientId;
    let createdAccountCode: string | null = null;
    let createdAccountName: string | null = null;

    const useNewAccount = lockProspect || (!resolvedClientId && Boolean(trimmedProspect));

    if (useNewAccount && trimmedProspect) {
      if (!trimmedProspect) {
        setError('Company name is required.');
        return;
      }
      const sp = formatSalespersonCode(salespersonCode);
      if (!sp) {
        setError(
          elevatedAccountCreate
            ? 'Select or enter a salesperson code for the new account (Admin can assign any code).'
            : 'Select a salesperson code for the new account.',
        );
        return;
      }
      const ownNormalized = normalizeSalespersonCodes(ownSalespersonCodes);
      if (!elevatedAccountCreate && !ownNormalized.includes(normalizeSalespersonCode(sp))) {
        setError('Pick one of your assigned salesperson codes.');
        return;
      }
    } else if (resolvedClientId && !clientById(linkableClients, resolvedClientId)) {
      setError('Select a valid account from the list.');
      return;
    }

    const resolvedDueLocal = parseDueDateInput(dueLocal) ?? dueLocal;
    const due_date = datetimeLocalSingaporeToIso(resolvedDueLocal);
    if (!due_date) {
      setError('Due date and time are required.');
      return;
    }

    if (showOutletField) {
      if (!outletInput.trim() && !outletName.trim()) {
        setError('Outlet / area is required for this account.');
        return;
      }
    }

    const resolvedOwner = resolveOwnerId();
    if (canAssignOwner && !assignableOwners.some((o) => o.value === resolvedOwner)) {
      setError('Select a valid owner.');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      // New customer → auto-create TEMP account under the user's salesperson code.
      if (useNewAccount && trimmedProspect) {
        const accountResult = await ensureProspectAccount({
          companyName: trimmedProspect,
          salespersonCode: formatSalespersonCode(salespersonCode),
          address,
          phone: normalizeStoredPhone(contactPhone),
          existingClients: data.clients,
          portal,
        });
        resolvedClientId = accountResult.clientId;
        createdAccountCode = accountResult.code;
        createdAccountName = accountResult.companyName;
      }

      const locationLabel = (outletInput.trim() || outletName.trim() || '').trim();
      const persistOutlet = Boolean(motherNavCode) && (showOutletField || Boolean(locationLabel));
      // Static SG catalog — persist as outlet_name only (no NAV outlet code / postal).
      const resolvedOutletName = persistOutlet && locationLabel ? locationLabel : null;

      const payload = {
        title: trimmedTitle,
        due_date,
        status,
        client_id: resolvedClientId || null,
        prospect_company_name: null,
        meeting_minute_id: meetingMinuteId.trim() || null,
        contact_person: contactPerson.trim() || null,
        contact_phone: normalizeStoredPhone(contactPhone) || null,
        address: address.trim() || null,
        outlet_code: null,
        outlet_name: resolvedOutletName,
        outlet_postal: null,
        description: trimmedDescription,
        owner_id: resolvedOwner,
      };

      const account =
        (resolvedClientId ? clientById(data.clients, resolvedClientId) : undefined) ??
        (createdAccountName && createdAccountCode
          ? {
              id: resolvedClientId,
              company_name: createdAccountName,
              code: createdAccountCode,
            }
          : undefined);

      if (isEdit && task) {
        const { data: updated, error: updateError } = await getDbSupabase()
          .from('tasks')
          .update(payload)
          .eq('id', task.id)
          .select('*')
          .single();
        if (updateError) throw updateError;
        const detail =
          buildActivityChangeDetail([
            activityChangeLine('Title', task.title ?? '', trimmedTitle),
            activityChangeLine('Status', task.status ?? '', status),
            activityChangeLine('Due date', task.due_date ?? '', due_date),
            activityChangeLine('Description', task.description ?? '', payload.description),
            activityChangeLine('Outlet', task.outlet_name ?? '', payload.outlet_name ?? ''),
            activityChangeLine(
              'Owner',
              ownerLabelForRef(data.users, task.owner_id),
              ownerLabelForRef(data.users, resolvedOwner),
            ),
          ]) ?? `Task "${trimmedTitle}" updated.`;
        logPortalActivityQuiet({
          portal,
          action: 'update',
          entityType: 'task',
          entityId: task.id,
          clientId: resolvedClientId || undefined,
          title: `Task updated: ${trimmedTitle}`,
          detail: account
            ? `${detail}\nAccount: ${account.company_name} (${account.code})${
                resolvedOutletName ? ` · Outlet: ${resolvedOutletName}` : ''
              }`
            : detail,
        });
        if (updated) {
          const saved = updated as Task;
          if (pendingFiles.length > 0) {
            await uploadPendingTaskAttachments({
              taskId: saved.id,
              files: pendingFiles,
              uploadedBy: username,
            });
            setPendingFiles([]);
          }
          patchTask(saved);
          onSaved?.(saved);
        }
      } else {
        const { data: created, error: insertError } = await getDbSupabase()
          .from('tasks')
          .insert(payload)
          .select('*')
          .single();
        if (insertError) throw insertError;
        const ownerLabel = ownerLabelForRef(data.users, resolvedOwner);
        logPortalActivityQuiet({
          portal,
          action: 'create',
          entityType: 'task',
          entityId: created?.id != null ? String(created.id) : undefined,
          clientId: resolvedClientId || undefined,
          title: `Task created: ${trimmedTitle}`,
          detail: account
            ? `Due ${due_date}. Owner: ${ownerLabel}. Account: ${account.company_name} (${account.code})${
                resolvedOutletName ? ` · Outlet: ${resolvedOutletName}` : ''
              }${createdAccountCode ? ' · auto-created TEMP account' : ''}.`
            : `Due ${due_date}. Owner: ${ownerLabel}.`,
        });
        if (created) {
          const saved = created as Task;
          if (pendingFiles.length > 0) {
            await uploadPendingTaskAttachments({
              taskId: saved.id,
              files: pendingFiles,
              uploadedBy: username,
            });
            setPendingFiles([]);
          }
          patchTask(saved);
          onSaved?.(saved);
        }
      }
      if (createdAccountCode) {
        void refresh({ silent: true, slices: ['clients'] });
      }
      onClose();
    } catch (err) {
      setError(formatSupabaseError(err));
    } finally {
      setSaving(false);
    }
  }

  const showProspectLocked = lockProspect && Boolean(defaultProspectName || prospectName);

  return (
    <Modal
      title={isEdit ? 'Edit task' : 'New task'}
      open={open}
      onClose={onClose}
      panelClassName="modal-panel--task-form"
    >
      <form className="crm-form crm-form--modal" onSubmit={(e) => void handleSave(e)}>
        <div className="crm-form__scroll">
          {error && <div className="alert alert-error">{error}</div>}

          <label className="field">
            <span className="field-label">Task title *</span>
            <InputWithClear value={title} onClear={() => setTitle('')}>
              <DemTypeahead
                className="crm-task-title-typeahead"
                inputClassName="field-input"
                value={title}
                onChange={setTitle}
                options={titleOptions}
                placeholder="Search or enter task title"
                ariaLabel="Task title"
              />
            </InputWithClear>
          </label>

          <label className="field">
            <span className="field-label">Due date &amp; time *</span>
            <DueDateTimeField value={dueLocal} onChange={setDueLocal} required />
          </label>

          <div className="crm-form__grid">
            <label className="field">
              <span className="field-label">Status</span>
              <select
                className="field-input"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
              >
                {TASK_STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </label>

            {canAssignOwner ? (
              <label className="field">
                <span className="field-label">Assign to *</span>
                <select
                  className="field-input"
                  value={ownerId}
                  onChange={(e) => setOwnerId(e.target.value)}
                  required
                >
                  {assignableOwners.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.display_name}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}
          </div>

          <div className="field">
            <span className="field-label">Related account</span>
            {showProspectLocked ? (
              <input
                className="field-input"
                type="text"
                value={`${prospectName} (${TEMP_ACCOUNT_CODE_PREFIX}…)`}
                disabled
                readOnly
                aria-label="New customer"
              />
            ) : lockClient && defaultClientId ? (
              <AccountTypeahead
                clients={linkableClients}
                value={clientId}
                onChange={applyClientId}
                disabled
                placeholder="Search accounts"
              />
            ) : hasLinkedAccount && !accountPickerOpen ? (
              <div className="crm-linked-account">
                <span className="crm-linked-account__label">
                  {linkedAccount
                    ? clientTypeaheadLabel(linkedAccount)
                    : 'Linked account'}
                </span>
                <div className="crm-linked-account__actions">
                  <button
                    type="button"
                    className="btn-secondary btn-sm"
                    disabled={saving}
                    onClick={startAccountChange}
                  >
                    Change
                  </button>
                  <button
                    type="button"
                    className="btn-secondary btn-sm"
                    disabled={saving}
                    onClick={clearRelatedAccount}
                  >
                    Clear
                  </button>
                </div>
              </div>
            ) : hasProspectDraft && !accountPickerOpen ? (
              <div className="crm-linked-account">
                <span className="crm-linked-account__label">
                  {prospectName}
                  <span className="crm-linked-account__tag">New account</span>
                </span>
                <div className="crm-linked-account__actions">
                  <button
                    type="button"
                    className="btn-secondary btn-sm"
                    disabled={saving}
                    onClick={startAccountChange}
                  >
                    Change
                  </button>
                  <button
                    type="button"
                    className="btn-secondary btn-sm"
                    disabled={saving}
                    onClick={clearRelatedAccount}
                  >
                    Clear
                  </button>
                </div>
              </div>
            ) : (
              <AccountTypeahead
                clients={linkableClients}
                value={clientId}
                onChange={applyClientId}
                allowNone
                allowCreate
                newAccountDraft={prospectName}
                onNewAccountName={applyNewAccountName}
                createOptionLabel={(name) => `Create new account: ${name}`}
                placeholder="Search account or enter company name"
              />
            )}
            <p className="field-hint">
              {showProspectLocked
                ? 'Creates a temporary account and links this task. Select the salesperson code that owns the account.'
                : lockClient && defaultClientId
                  ? 'The account is fixed for this visit task.'
                  : 'Optional. Select an existing account, or enter a new name to create a temporary account automatically.'}
            </p>
          </div>

          {showOutletField ? (
            <label className="field">
              <span className="field-label">Outlet / area *</span>
              <InputWithClear
                value={outletInput}
                onClear={() => applyOutletInput('')}
              >
                <DemTypeahead
                  className="crm-task-outlet-typeahead"
                  inputClassName="field-input"
                  value={outletInput}
                  onChange={applyOutletInput}
                  options={outletTypeaheadOptions}
                  tableMode="location"
                  placeholder="Search area, mall, or MRT"
                  ariaLabel="Outlet or area"
                  disabled={saving}
                />
              </InputWithClear>
              <p className="field-hint">
                Required. Search Singapore areas, malls, or MRT stations — or type a custom name.
              </p>
            </label>
          ) : null}

          {showSalespersonPicker ? (
            <label className="field">
              <span className="field-label">Salesperson code *</span>
              {salespersonChoices.length > 0 ? (
                <select
                  className="field-input"
                  value={salespersonCode}
                  onChange={(e) => setSalespersonCode(e.target.value)}
                  required
                >
                  {!salespersonCode ? (
                    <option value="" disabled>
                      Select salesperson code
                    </option>
                  ) : null}
                  {salespersonChoices.map((code) => (
                    <option key={code} value={code}>
                      {code}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  className="field-input mono"
                  value={salespersonCode}
                  onChange={(e) => setSalespersonCode(e.target.value)}
                  placeholder={
                    elevatedAccountCreate
                      ? 'Enter a salesperson code'
                      : 'Your salesperson code'
                  }
                  required
                />
              )}
              <p className="field-hint">
                {elevatedAccountCreate
                  ? 'From User Management salesperson codes.'
                  : salespersonChoices.length > 1
                    ? 'You have multiple salesperson codes. Select which code this new account belongs to.'
                    : 'New account will be owned by this salesperson code.'}
              </p>
            </label>
          ) : null}

          <label className="field">
            <span className="field-label">Contact person</span>
            <InputWithClear
              value={contactPerson}
              onClear={() => {
                setContactPerson('');
                setContactPhone('');
              }}
            >
              <DemTypeahead
                className="crm-task-contact-typeahead"
                inputClassName="field-input"
                value={contactPerson}
                onChange={setContactPerson}
                onCommit={handleContactCommit}
                options={contactOptions}
                placeholder={
                  clientId && !canPickCrmContacts
                    ? 'Enter a name (CRM contacts are hidden for this account)'
                    : clientId
                      ? 'Search contacts or enter a new name'
                      : 'Enter the contact person for this visit'
                }
                ariaLabel="Contact person"
              />
            </InputWithClear>
            {clientId && !canPickCrmContacts ? (
              <p className="field-hint">
                This account is outside your salesperson codes. CRM contact search is disabled.
                Admin, IT or Management may assign CRM contacts. You may still enter a visit name
                without browsing the directory.
              </p>
            ) : null}
          </label>

          {showContactPhone ? (
            <label className="field">
              <span className="field-label">Phone number</span>
              <IntlPhoneField value={contactPhone} onChange={setContactPhone} />
            </label>
          ) : null}

          <label className="field">
            <span className="field-label">Address</span>
            <InputWithClear
              value={address}
              onClear={() => {
                setAddress('');
                addressTouchedRef.current = true;
              }}
            >
              <textarea
                className="field-input"
                rows={2}
                value={address}
                onChange={(e) => {
                  setAddress(e.target.value);
                  addressTouchedRef.current = true;
                }}
                placeholder="Defaults to the account office address (or ACRA registered if office is empty); edits apply to this task only"
              />
            </InputWithClear>
            <p className="field-hint">Saved on this task only. Does not update the account profile.</p>
          </label>

          <label className="field">
            <span className="field-label">Description / notes *</span>
            <InputWithClear value={description} onClear={() => setDescription('')}>
              <textarea
                className="field-input"
                rows={4}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter additional details"
                required
              />
            </InputWithClear>
          </label>

          <TaskAttachmentsField
            taskId={task?.id ?? null}
            canEdit
            uploadedBy={username}
            pendingFiles={pendingFiles}
            onPendingFilesChange={setPendingFiles}
          />

          {showNewCustomerFields &&
          !elevatedAccountCreate &&
          salespersonChoices.length === 0 ? (
            <div className="alert alert-warn">
              No salesperson code on your profile. Ask Admin Hub to assign one before creating a new
              customer from a task.
            </div>
          ) : null}
        </div>

        <div className="crm-form__actions">
          <button type="button" className="btn-secondary" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="submit" className="btn-primary" disabled={saving}>
            {saving ? 'Saving…' : 'Save task'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
